import { EnumCommitFieldStructure } from 'apps/orion/src/app/models/supply-visibility.model';
import { MappedField } from 'libs/datagrid/src/lib/containers/dx-datagrid/dx-datagrid.types';
import { FormSetting } from '../store/carriers-list/carriers-list.types';
import { Commit as CommitModel, EnumRequestStatusStatus } from './supply-visibility.model';
import { hasAllowedFields } from './field-structure.utils';

export type VendorName = {
  vendorCode: string;
  vendorName: string;
};

export type Carriers = {
  code: string;
  id: string;
  lastModifiedBy: string;
  lastModifiedDate: string;
  name: string;
};

export type Commit = {
  [key: string]:
    | boolean
    | Carriers
    | Countries
    | DummyCommitDetail
    | null
    | number
    | Reason
    | string
    | TransportType
    | undefined
    | unknown;
  actualETADate?: string;
  apsRelevant?: string;
  arrivalDate?: string;
  asn: number;
  batchNo?: string;
  codeDate?: string;
  containerNumber: string;
  countryOfOrigin?: string;
  createDate?: string;
  creator?: string;
  deliveryDate?: string;
  dummyCommit?: DummyCommitDetail;
  dummyCommitHeaderID?: string;
  eddDate?: string;
  editBy?: string;
  etaDate: string;
  etaPortDate?: string;
  etdDate: string;
  expirationDate?: string;
  expressFlag?: string;
  extApproval?: string;
  externalPurchaseOrderLine?: string;
  externalPurchaseOrderNumber?: string;
  forwarder?: string;
  hold?: string;
  id?: string;
  inboundDeliveryDate?: string;
  inboundDeliveryNumber?: string;
  invalidEta?: boolean;
  invoiceNumber?: string;
  isModifyByDummy?: boolean;
  isUsedInETA?: boolean;
  manufacturer?: string;
  mfgPartner?: string;
  mpn?: string;
  msrRemarks?: string;
  parentID?: string;
  parentInboundDeliveryNumber?: string;
  partNumber: string;
  partNumberDescription?: string;
  plant?: string;
  precedingInboundDeliveryNumber?: string;
  purchaseOrderLine: string;
  purchaseOrderNumber: string;
  quantity: number;
  reason?: number;
  reasonDetail?: string;
  receiveDate?: string;
  recomitRequestDate: string;
  remainingQuantity?: number;
  remark?: string;
  requestDate?: string;
  responsibility?: string;
  rtv?: string;
  scheduleLine?: string;
  scheduleLineDate?: string;
  shipped: boolean;
  shortageComment?: string;
  slotCode?: string;
  slotDate?: string;
  status?: string;
  thirdPartyPaid?: string;
  trackNumber: string;
  triggerDate?: string;
  typeOfTransportation?: string;
  unitWeight: number;
  vendorCode?: string;
  vendorName?: string;
  withMergeModifyQuantity?: boolean;
};

export type DummyCommitHeadersFilter = {
  partNumber?: string;
  vendorCode?: string;
};

type DummyCommitDetail = {
  actualETADate?: string;
  apsRelevant?: string;
  arrivalDate?: string;
  asn: number;
  batchNo?: string;
  chooseVendor?: string;
  codeDate?: string;
  containerNumber: string;
  countryOfOrigin?: string;
  creator?: string;
  deliveryDate: string;
  dummyCommitHeaderID: string;
  eddDate?: string;
  etaDate: string;
  etaPortDate?: string;
  etdDate: string;
  expirationDate?: string;
  expressFlag?: string;
  forwarder?: string;
  hasDocuments?: boolean;
  id?: string;
  inboundDeliveryNumber: string;
  invalidEta: boolean;
  invoiceNumber?: string;
  isUsedInETA: boolean;
  manufacturer?: string;
  mfgPartner?: string;
  mpn?: string;
  msrRemarks?: string;
  partNumber: string;
  purchaseOrderLine: string;
  purchaseOrderNumber: string;
  quantity: number;
  reason?: number;
  reasonDetail?: string;
  recomitRequestDate: string;
  remark?: string;
  requestDate?: string;
  scheduleLine?: string;
  scheduleLineDate?: string;
  shipped: boolean;
  shortageComment?: string;
  slotDate?: string;
  status?: string;
  stockType?: string;
  thirdPartyPaid?: string;
  trackNumber: string;
  triggerDate?: string;
  typeOfTransportation?: string;
  unitWeight: number;
  vendorCode: string;
};

export type MergeCommit = {
  inboundDeliveriesToMerge: string[];
  inboundDeliveryWhereToMerge: string;
};

export type CommitProperty = {
  id: keyof Commit | string;
  name: string;
  type: 'boolean' | 'custom' | 'date' | 'dropdown' | 'number' | 'string';
};

export type Countries = {
  code: string;
  id: string;
  lastModifiedBy: string;
  lastModifiedDate: string;
  name: string;
};

export type TransportType = {
  id: string;
  name: string;
};

export type FileData = {
  description: string;
  file: File;
};

export type FormKey = {
  formKey: string;
  rights: Rights[];
};

export type Rights = {
  field?: string;
  role?: Role[];
};

export type Role = {
  isUsed?: boolean;
  role?: string;
};

export type UpdatedValue = {
  formKey: string;
  value: FormSetting[];
};

export type Reason = {
  id: number;
  name: string;
};

export type CommitTransactionDetails = {
  data?: CommitModel | CommitModel[];
  datagridInfo?: DatagridInfo;
  id: string;
  isCache?: boolean;
  type?: EnumCommitType;
};

export enum EnumCommitType {
  NEW = 0,
  EDIT = 1,
  MERGE = 2,
  SPLIT = 3,
}

export enum EnumCustomEdit {
  NONE = 0,
  REMERGE = 1,
  //ADD other custom edit actions
}

export type DialogDetail = {
  dialogClose?: boolean;
  inboundDeliveryNumber?: string;
};

export type DatagridInfo = {
  /**
   * @description Sets index of commit by finding it fromAPI response
   */
  index?: number;
  /**
   * @description Indicates if we need to set page number by finding it from API response
   */
  isPageNumberNeeded?: boolean;
  /**
   * @description Sets directly from datagrid (No need to find it from API response)
   */
  pageNumber?: number;
};

export type DocumentNameDto = {
  dateCreated?: string;
  description?: string;
  name?: string;
};

export type CommitHistoryNameValueDto = {
  name?: string;
  value?: string;
};

export type CommitHistoryDto = {
  commitHistories?: CommitHistory[];
  createBy?: string;
  createDate?: string;
  mergedCommitHistory?: MergedCommitHistoryDto;
  type?: string;
};

export type CommitHistory = {
  changeBy?: string;
  changeDate?: string;
  field?: string;
  inboundDeliveryNumber?: string;
  newValue?: string;
  oldValue?: string;
  sapField?: string;
  sapLocation?: string;
  transaction?: string;
};

type MergedCommitHistoryDto = {
  byTypeDesc?: string;
  createdBy?: string;
  dateCreated?: string;
  dateModified?: string;
  flags?: string[];
  id?: string;
  mergedCommitsFrom?: MergedCommits[];
  mergedCommitsTo?: MergedCommits[];
  modifiedBy?: string;
  type?: string;
};

type MergedCommits = {
  etaDate?: string;
  inboundDeliveryNumber?: string;
  originalQuantity?: number;
  plannedNumber: string;
  quantity?: number;
  requestedDate: string;
  triggerDate: string;
};

export type ManualSplitCommitItemDto = {
  deliveryDate?: string;
  etaDate?: string;
  fields?: Record<string, unknown>;
  quantity?: number;
};

export type ManualSplitCommitsDto = {
  inboundDeliveryNumber?: string;
  splitCommitItems?: ManualSplitCommitItemDto[];
};

export type RequestStatusDto = RequestStatus & {
  consumer?: string;
};

export type RequestStatus = {
  message: string;
  requestHash: string;
  status: EnumRequestStatusStatus;
};

export enum CommitOperationType {}

export type CommitModuleRedirectDTO = {
  inboundDeliveryNumbers?: string;
  includeParentIDs?: string[];
  status?: string[];
};

export type CommitPlaceHolderTypeVal = {
  bulkEditForm?: boolean | number | string;
  commitForm?: boolean | number | string;
  commitHistory?: boolean | number | string;
  createCDForm?: boolean | number | string;
  createCSForm?: boolean | number | string;
  datagrid?: boolean | number | string;
  editCDForm?: boolean | number | string;
  editCSForm?: boolean | number | string;
  splitForm?: boolean | number | string;
  templateFilterCreateForm?: boolean | number | string;
  templateFilterEditForm?: boolean | number | string;
};
export type CommitFields = {
  /**
   * Required for commit form
   */
  controlType?: CommitPlaceHolderTypeVal;
  /**
   * Shows dataType tah
   */
  dataType?: CommitPlaceHolderTypeVal;
  editType?: string;
  /**
   * Shows if field should be excluded in specific forms
   */
  exclude?: CommitPlaceHolderTypeVal;
  /**
   * (Commit Form)
   */
  exclusive?: string;
  format?: string;
  /**
   * Shows the name that should be displayed in forms
   */
  headerText?: CommitPlaceHolderTypeVal;
  /**
   * Used as field in forms
   */
  id: CommitPlaceHolderTypeVal;
  /**
   * (Datagrid)
   */
  isPrimaryKey?: boolean;
  /**
   * Shows if the field is mandatory in forms
   */
  isRequired?: CommitPlaceHolderTypeVal;
  /**
   * Shows if it should be visible by default in forms
   */
  visible?: CommitPlaceHolderTypeVal;
};

export class CommonCommitFields {
  fields: MappedField[];
  userRightFields: string[] = [];
  hasField: (
    field: CommitFields,
    refId?: keyof CommitPlaceHolderTypeVal,
    actualId?: keyof CommitPlaceHolderTypeVal,
  ) => boolean;

  constructor(type?: EnumCommitFieldStructure, userRightFields?: string[]) {
    if (hasAllowedFields(userRightFields)) {
      this.userRightFields = userRightFields.map((e): string => e.toLowerCase());
      this.hasField = this.validFunWithUserFields;
    } else {
      this.hasField = this.validFunWithoutUserFields;
    }
    this.fields = this.getDefaultFields(type);
  }

  getDefaultFields(type?: EnumCommitFieldStructure): MappedField[] {
    switch (type) {
      case EnumCommitFieldStructure.CommitTable:
        return this.getDatagridFields();
      case EnumCommitFieldStructure.CommitForm:
        return this.getCommitFormFields();
      case EnumCommitFieldStructure.SplitForm:
        return this.getSplitFormFields();
      case EnumCommitFieldStructure.BulkEditForm:
        return this.getBulkFormFields();
      case EnumCommitFieldStructure.DocumentTable:
        return this.getCommitDocumentsFields();
      case EnumCommitFieldStructure.DocumentCreateForm:
        return this.getCDFormField();
      case EnumCommitFieldStructure.DocumentEditForm:
        return this.getCDEditFormFields();
      case EnumCommitFieldStructure.ChartSettingsTable:
        return this.getChartSettingsFields();
      case EnumCommitFieldStructure.ChartSettingsCreateForm:
        return this.getCSFormField();
      case EnumCommitFieldStructure.ChartSettingsEditForm:
        return this.getCSEditFormFields();
      case EnumCommitFieldStructure.CommitHistoryTable:
        return this.getCommitHistoryTableFields();
      case EnumCommitFieldStructure.TemplateFilterTable:
        return this.getTemplateFiltersFields();
      case EnumCommitFieldStructure.TemplateFilterCreateForm:
        return this.getTemplateFiltersFormFields();
      case EnumCommitFieldStructure.TemplateFilterEditForm:
        return this.getTemplateFiltersEditFormFields();
      default:
        return this.getDatagridFields();
    }
  }

  /**
   *
   * @param field field which inclusiveness needs to check
   * @param refId Id
   * @param actualId actual Id on which fields belongs
   * @returns is valid field
   */
  validFunWithUserFields = (
    field: CommitFields,
    refId?: keyof CommitPlaceHolderTypeVal,
    actualId?: keyof CommitPlaceHolderTypeVal,
  ): boolean => {
    if (
      this.userRightFields &&
      !(field.isRequired && actualId && field.isRequired[actualId]) //For adding fields not present in default fields
    ) {
      // return this.userRightFields.includes((String(field.id[refId])).toLowerCase());
      return (
        this.userRightFields.includes(String((refId && field.id[refId]) ?? '').toLowerCase()) &&
        !(field.exclude && actualId && field.exclude[actualId] === true)
      ); //2571 Comment
    } else {
      return this.validFunWithoutUserFields(field, refId, actualId);
    }
  };
  validFunWithoutUserFields = (
    field: CommitFields,
    refId?: keyof CommitPlaceHolderTypeVal,
    actualId?: keyof CommitPlaceHolderTypeVal,
  ): boolean => {
    if (field.exclude && actualId && field.exclude[actualId] === true) {
      return false;
    } else {
      return true;
    }
  };

  getDatagridFields(): MappedField[] {
    return this.commonCommitFields
      .filter((field: CommitFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: CommitFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isPrimaryKey: !!field.isPrimaryKey,
          isRequired: !!field.isRequired?.datagrid,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }
  getCommitFormFields(): MappedField[] {
    return this.commonCommitFields
      .filter((field: CommitFields): boolean => {
        return this.hasField(field, 'datagrid', 'commitForm');
      })
      .map((field: CommitFields): MappedField => {
        return {
          controlType: field.controlType?.commitForm
            ? (field.controlType.commitForm as string)
            : 'simple',
          exclusive: field.exclusive,
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.commitForm
            ? field.headerText.commitForm
            : field.headerText?.datagrid) as string,
          isRequired: !!field.isRequired?.commitForm,
          type: field.dataType?.commitForm ? (field.dataType.commitForm as string) : 'text',
          visible: !!field.visible?.commitForm,
        };
      });
  }

  createDefaultField(field: string): CommitFields {
    return {
      dataType: {
        splitForm: 'text',
      },
      headerText: {
        splitForm: field,
      },
      id: { datagrid: field, splitForm: field },
    } as CommitFields;
  }

  getSplitFormFields(): MappedField[] {
    //Custom logic
    const totalFields: CommitFields[] = this.commonCommitFields.slice(); // create a copy of the array

    for (const defaultField of this.userRightFields) {
      const index: number = this.commonCommitFields.findIndex((field: CommitFields): boolean => {
        return String(field.id?.datagrid ?? '').toLowerCase() === defaultField.toLowerCase();
      });
      if (index === -1) {
        totalFields.push(this.createDefaultField(defaultField));
      }
    }

    return totalFields
      .filter((field: CommitFields): boolean => {
        return this.userRightFields.includes(String(field.id?.datagrid ?? '').toLowerCase());
      })
      .map((field: CommitFields): MappedField => {
        return {
          field: (field.id?.splitForm as string) ?? '',
          headerText: (field.headerText?.splitForm as string) ?? '',
          type: field.dataType?.splitForm ? (field.dataType.splitForm as string) : 'string',
          visible: !!field.visible?.splitForm,
        };
      });
  }

  getBulkFormFields(): MappedField[] {
    const ccFields: CommitFields[] = this.commonCommitFields.slice();
    for (const dField of this.userRightFields) {
      const index: number = this.commonCommitFields.findIndex((field: CommitFields): boolean => {
        return String(field.id?.datagrid ?? '').toLowerCase() === dField.toLowerCase();
      });
      if (index === -1) {
        ccFields.push(this.createDefaultField(dField));
      }
    }
    return ccFields
      .filter((field: CommitFields): boolean => {
        return this.userRightFields.includes(String(field.id?.datagrid ?? '').toLowerCase());
      })
      .map((field: CommitFields): MappedField => {
        return {
          field: (field.id?.splitForm as string) ?? '',
          headerText: (field.headerText?.splitForm as string) ?? '',
          type: field.dataType?.splitForm ? (field.dataType.splitForm as string) : 'string',
          visible: !!field.visible?.splitForm,
        };
      });
  }

  getCommitHistoryTableFields(): MappedField[] {
    return this.commonCommitFields
      .filter((field: CommitFields): boolean => {
        return this.hasField(field, 'commitHistory');
      })
      .map((field: CommitFields): MappedField => {
        return {
          field: (field.id?.commitHistory as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          visible: !!field.visible?.commitHistory,
        };
      });
  }

  getFormFieldByCase(key: string, toUppercase?: boolean): string {
    const fields: CommitFields[] = this.commonCommitFields;
    const field: CommitFields | undefined = fields.find((item: CommitFields): boolean => {
      const comparer: boolean | number | string | undefined = toUppercase
        ? item.id.splitForm
        : item.id.datagrid;
      if (comparer && String(comparer).toLowerCase() === key.toLowerCase()) {
        return true;
      }
      return false;
    });
    if (!field) return '';
    return String(toUppercase ? field.id.splitForm : field.id.datagrid);
  }

  public readonly commonCommitFields: CommitFields[] = [
    {
      dataType: { datagrid: 'actions' },
      exclude: { bulkEditForm: true, commitForm: true },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { datagrid: 'status' },
      exclusive: 'edit',
      headerText: { datagrid: 'Status', splitForm: 'Status' },
      id: { commitHistory: 'Status', datagrid: 'status', splitForm: 'status' },
      visible: { datagrid: true },
      // exclude: { bulkEditForm: true},
    },
    {
      controlType: { commitForm: 'date' }, //editType on ColumnSetting
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: {
        datagrid: 'Inbound Delivery Date',
        splitForm: 'Inbound Delivery Date',
      },
      id: {
        commitHistory: 'InboundDeliveryDate',
        datagrid: 'inboundDeliveryDate',
        splitForm: 'InboundDeliveryDate',
      },
      visible: { commitForm: false, datagrid: true },
    },
    {
      controlType: { commitForm: 'number' }, ///
      dataType: { commitForm: 'number', datagrid: 'string' },
      exclusive: 'edit',
      headerText: { commitForm: 'Inbound Delivery', datagrid: 'ID', splitForm: 'ID' },
      id: {
        commitHistory: 'InboundDeliveryNumber',
        datagrid: 'inboundDeliveryNumber',
        splitForm: 'inboundDeliveryNumber',
      },
      isPrimaryKey: true,
      // exclude: { bulkEditForm: true}, //ADDED RECENTLY
      visible: { commitForm: true, datagrid: true }, //ADDED RECENTLY
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: { datagrid: 'ETD Date', splitForm: 'ETD Date' },
      id: { commitHistory: 'ETDDate', datagrid: 'etdDate', splitForm: 'ETDDate' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: { datagrid: 'ETA Date', splitForm: 'ETA Date' },
      id: { commitHistory: 'ETADate', datagrid: 'etaDate', splitForm: 'ETADate' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: { datagrid: 'Delivery Date', splitForm: 'Delivery Date' },
      id: { commitHistory: 'DeliveryDate', datagrid: 'deliveryDate', splitForm: 'DeliveryDate' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'd-poLine' },
      dataType: { datagrid: 'string' },
      headerText: { commitForm: 'Purchase Order Line', datagrid: 'PO Line', splitForm: 'PO Line' },
      id: {
        commitHistory: 'PurchaseOrderLine',
        datagrid: 'purchaseOrderLine',
        splitForm: 'PurchaseOrderLine',
      },
      isRequired: { commitForm: true, datagrid: false },
      visible: { commitForm: true, datagrid: true },
      // exclude: { bulkEditForm: true},
    },
    {
      controlType: { commitForm: 'd-poNumber' },
      dataType: { datagrid: 'string' },
      exclude: { commitForm: true },
      headerText: { commitForm: 'PO', datagrid: 'PO', splitForm: 'PO' },
      id: {
        commitHistory: 'PO',
        datagrid: 'purchaseOrderNumber',
        splitForm: 'purchaseOrderNumber',
      },
      visible: { datagrid: true },
    },

    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'number', datagrid: 'number', splitForm: 'number' },
      headerText: {
        commitForm: 'Delivery Quantity',
        datagrid: 'Delivery Qty',
        splitForm: 'Delivery Qty',
      },
      id: { commitHistory: 'Quantity', datagrid: 'quantity', splitForm: 'quantity' },
      isRequired: { commitForm: true, datagrid: false },
      visible: { commitForm: true, datagrid: true },
      // exclude: { bulkEditForm: true},
    },
    {
      controlType: { commitForm: 'd-partNumber' },
      dataType: { commitForm: 'number', datagrid: 'string' },
      headerText: { datagrid: 'Part Number', splitForm: 'Part Number' },
      id: { commitHistory: 'PartNumber', datagrid: 'partNumber', splitForm: 'partNumber' },
      isRequired: { commitForm: true, datagrid: false }, //
      visible: { commitForm: true, datagrid: true },
      // exclude: { bulkEditForm: true},
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { datagrid: 'string' },
      exclude: { commitForm: true },
      headerText: { commitForm: 'Vendor Code', datagrid: 'Vendor', splitForm: 'Vendor' },
      id: { commitHistory: 'VendorCode', datagrid: 'vendorCode', splitForm: 'vendorCode' },
      visible: { datagrid: true },
    },
    {
      controlType: { commitForm: 'simple-np' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'Track No.', splitForm: 'Track No.' },
      id: { commitHistory: 'TrackNumber', datagrid: 'trackNumber', splitForm: 'TrackNumber' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple-np' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'Container No.', splitForm: 'Container No.' },
      id: {
        commitHistory: 'ContainerNumber',
        datagrid: 'containerNumber',
        splitForm: 'ContainerNumber',
      },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: {
        commitForm: 'text',
        datagrid: 'invoice',
        splitForm: 'string',
      },
      headerText: { datagrid: 'Invoice No.', splitForm: 'Invoice No.' },
      id: { commitHistory: 'InvoiceNumber', datagrid: 'invoiceNumber', splitForm: 'InvoiceNumber' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { datagrid: 'string' },
      exclude: { commitForm: true },
      headerText: { datagrid: 'Parent ID', splitForm: 'Parent ID' },
      id: { datagrid: 'parentID', splitForm: 'parentID' },
      visible: { datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { datagrid: 'requestType' },
      exclude: { commitForm: true },
      headerText: { datagrid: 'Request Type', splitForm: 'Request Type' },
      id: { commitHistory: 'RequestType', datagrid: 'RequestType', splitForm: 'RequestType' },
      visible: { datagrid: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: { datagrid: 'Actual ETA Date', splitForm: 'Actual ETA Date' },
      id: { commitHistory: 'ActualETADate', datagrid: 'actualETADate', splitForm: 'ActualETADate' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: { datagrid: 'Expiration Date', splitForm: 'Expiration Date' },
      id: {
        commitHistory: 'ExpirationDate',
        datagrid: 'expirationDate',
        splitForm: 'expirationDate',
      },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'dateTime' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'dateTime',
      },
      headerText: { datagrid: 'Slot Date', splitForm: 'Slot Date' },
      id: { commitHistory: 'SlotDate', datagrid: 'slotDate', splitForm: 'SlotDate' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      }, //change in bulkEditForm
      headerText: { datagrid: 'ETA Port Date', splitForm: 'ETA Port Date' },
      id: { commitHistory: 'ETAPortDate', datagrid: 'etaPortDate', splitForm: 'ETAPortDate' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: {
        datagrid: 'Recommit Request Date',
        splitForm: 'Recommit Request Date',
      },
      id: {
        commitHistory: 'RecomitRequestDate',
        datagrid: 'recomitRequestDate',
        splitForm: 'RecomitRequestDate',
      },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'number' },
      headerText: { datagrid: 'Weight Unit', splitForm: 'Weight Unit' },
      id: { commitHistory: 'WeightUnit', datagrid: 'unitWeight', splitForm: 'UnitWeight' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: {
        datagrid: 'Schedule Line Date',
        splitForm: 'Schedule Line Date',
      },
      id: {
        commitHistory: 'ScheduleLineDate',
        datagrid: 'scheduleLineDate',
        splitForm: 'scheduleLineDate',
      },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple-np' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'Schedule Line', splitForm: 'Schedule Line' },
      id: { commitHistory: 'ScheduleLine', datagrid: 'scheduleLine', splitForm: 'scheduleLine' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'checkbox' },
      dataType: {
        commitForm: 'text',
        datagrid: 'checkbox',
        splitForm: 'boolean',
      },
      headerText: {
        commitForm: 'Is used in ETA',
        datagrid: 'ETA Used',
        splitForm: 'ETA Used',
      },
      id: { commitHistory: 'IsUsedInETA', datagrid: 'isUsedInETA', splitForm: 'IsUsedInETA' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'checkbox' },
      dataType: {
        commitForm: 'text',
        datagrid: 'checkbox',
        splitForm: 'boolean',
      },
      headerText: { datagrid: 'Shipped', splitForm: 'Shipped' },
      id: { commitHistory: 'Shipped', datagrid: 'shipped', splitForm: 'Shipped' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'checkbox' },
      dataType: {
        datagrid: 'checkbox',
        // commitForm: 'text',
        splitForm: 'boolean',
      },
      headerText: {
        commitForm: 'Invalid ETA',
        datagrid: 'Invalid ETA',
        splitForm: 'Invalid ETA',
      },
      id: { commitHistory: 'InvalidEta', datagrid: 'invalidEta', splitForm: 'invalidEta' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'dateTime' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'dateTime',
      },
      headerText: { datagrid: 'Receipt Date', splitForm: 'Receipt Date' },
      id: { commitHistory: 'ReceiptDate', datagrid: 'receiveDate', splitForm: 'ReceiveDate' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'checkbox' },
      dataType: {
        commitForm: 'text',
        datagrid: 'checkbox',
        splitForm: 'boolean',
      },
      headerText: { datagrid: 'ASN', splitForm: 'ASN' },
      id: { commitHistory: 'ASN', datagrid: 'asn', splitForm: 'ASN' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'Manufacturer', splitForm: 'Manufacturer' },
      id: { commitHistory: 'Manufacturer', datagrid: 'manufacturer', splitForm: 'Manufacturer' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'd-countryOfOrigin' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'custom' },
      headerText: {
        commitForm: 'Country Of Origin',
        datagrid: 'CoO',
        splitForm: 'CoO',
      },
      id: {
        commitHistory: 'CountryOfOrigin',
        datagrid: 'countryOfOrigin',
        splitForm: 'CountryOfOrigin',
      },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: { datagrid: 'Request Date', splitForm: 'Request Date' },
      id: { commitHistory: 'RequestDate', datagrid: 'requestDate', splitForm: 'RequestDate' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: { datagrid: 'Trigger Date', splitForm: 'Trigger Date' },
      id: { commitHistory: 'TriggerDate', datagrid: 'triggerDate', splitForm: 'TriggerDate' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: { commitForm: 'text', datagrid: 'dateUnchanged' },
      exclusive: 'edit',
      headerText: { datagrid: 'Last Update Date', splitForm: 'Last Update Date' },
      id: {
        commitHistory: 'LastUpdateDate',
        datagrid: 'lastUpdateDate',
        splitForm: 'lastUpdateDate',
      },
      visible: { commitForm: false, datagrid: true },
      // exclude: { bulkEditForm: true},
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { datagrid: 'string' },
      exclude: { commitForm: true },
      headerText: { datagrid: 'Last Update Name', splitForm: 'Last Update Name' },
      id: {
        commitHistory: 'ResponsibleEmail',
        datagrid: 'responsibleEmail',
        splitForm: 'responsibleEmail',
      },
      visible: { datagrid: true },
    },
    {
      controlType: { commitForm: 'd-mpn' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'MPN', splitForm: 'MPN' },
      id: { commitHistory: 'MPN', datagrid: 'mpn', splitForm: 'MPN' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'MFG Partner', splitForm: 'MFG Partner' },
      id: { commitHistory: 'MFGPartner', datagrid: 'mfgPartner', splitForm: 'MFGPartner' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'Batch No.', splitForm: 'Batch No.' },
      id: { commitHistory: 'BatchNo', datagrid: 'batchNo', splitForm: 'BatchNo' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'd-forwarder' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'custom' },
      headerText: { datagrid: 'Forwarder', splitForm: 'Forwarder' },
      id: { commitHistory: 'Forwarder', datagrid: 'forwarder', splitForm: 'Forwarder' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'Remark', splitForm: 'Remark' },
      id: { commitHistory: 'Remark', datagrid: 'remark', splitForm: 'Remark' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'Preceding ID', splitForm: 'Preceding ID' },
      id: {
        commitHistory: 'precedingInboundDeliveryNumber',
        datagrid: 'precedingInboundDeliveryNumber',
        splitForm: 'precedingInboundDeliveryNumber',
      },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'External PO', splitForm: 'External PO' },
      id: {
        commitHistory: 'externalPurchaseOrderNumber',
        datagrid: 'externalPurchaseOrderNumber',
        splitForm: 'externalPurchaseOrderNumber',
      },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'External PO Line', splitForm: 'External PO Line' },
      id: {
        commitHistory: 'externalPurchaseOrderLine',
        datagrid: 'externalPurchaseOrderLine',
        splitForm: 'externalPurchaseOrderLine',
      },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'dropdown' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'dropdown' },
      headerText: { datagrid: 'Responsibility', splitForm: 'Responsibility' },
      id: {
        commitHistory: 'responsibility',
        datagrid: 'responsibility',
        splitForm: 'responsibility',
      },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'dropdown' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'dropdown' },
      headerText: { datagrid: 'RTV', splitForm: 'RTV' },
      id: { commitHistory: 'rtv', datagrid: 'rtv', splitForm: 'rtv' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'dropdown' },
      dataType: {
        commitForm: 'text',
        datagrid: 'string',
        splitForm: 'dropdown',
      },
      headerText: { datagrid: 'Express Flag', splitForm: 'Express Flag' },
      id: { commitHistory: 'ExpressFlag', datagrid: 'expressFlag', splitForm: 'ExpressFlag' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'dropdown' },
      dataType: {
        commitForm: 'text',
        datagrid: 'string',
        splitForm: 'dropdown',
      },
      headerText: { datagrid: 'Third Party Paid', splitForm: 'Third Party Paid' },
      id: {
        commitHistory: 'ThirdPartyPaid',
        datagrid: 'thirdPartyPaid',
        splitForm: 'ThirdPartyPaid',
      },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'd-typeOfTransportation' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'custom' },
      headerText: {
        commitForm: 'Type Of Transportation',
        datagrid: 'Transport',
        splitForm: 'Transport',
      },
      id: {
        commitHistory: 'TypeOfTransportation',
        datagrid: 'typeOfTransportation',
        splitForm: 'TypeOfTransportation',
      },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'ROP Reason', splitForm: 'ROP Reason' },
      id: { commitHistory: 'MSRRemarks', datagrid: 'msrRemarks', splitForm: 'MSRRemarks' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: {
        datagrid: 'Shortage Comment',
        splitForm: 'Shortage Comment',
      },
      id: {
        commitHistory: 'ShortageComment',
        datagrid: 'shortageComment',
        splitForm: 'ShortageComment',
      },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'd-reason' },
      dataType: {
        commitForm: 'text',
        datagrid: 'custom',
        splitForm: 'dropdown',
      }, // change in splitForm
      headerText: { datagrid: 'Reason', splitForm: 'Reason' },
      id: { commitHistory: 'Reason', datagrid: 'reason', splitForm: 'Reason' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'Reason Detail', splitForm: 'Reason Detail' },
      id: { commitHistory: 'ReasonDetail', datagrid: 'reasonDetail', splitForm: 'reasonDetail' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: { datagrid: 'EDD Date', splitForm: 'EDD Date' },
      id: { commitHistory: 'EDDDate', datagrid: 'eddDate', splitForm: 'EDDDate' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'dropdown' },
      dataType: {
        commitForm: 'text',
        datagrid: 'string',
        splitForm: 'dropdown',
      },
      headerText: {
        commitForm: 'APS Relevant',
        datagrid: 'APS',
        splitForm: 'APS',
      },
      id: { commitHistory: 'APSRelevant', datagrid: 'apsRelevant', splitForm: 'APSRelevant' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'Revision', splitForm: 'Revision' },
      id: { commitHistory: 'Revision', datagrid: 'revision', splitForm: 'Revision' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'number', datagrid: 'number' },
      exclusive: 'edit',
      headerText: {
        commitForm: 'Request Quantity',
        datagrid: 'Request Qty',
        splitForm: 'Request Qty',
      },
      id: {
        commitHistory: 'RequestQuantity',
        datagrid: 'requestQuantity',
        splitForm: 'requestQuantity',
      },
      visible: { commitForm: false, datagrid: true },
      // exclude: { bulkEditForm: true},
    },
    {
      controlType: { commitForm: 'd-chooseVendor' },
      dataType: { commitForm: 'number' },
      exclude: { datagrid: true },
      headerText: { datagrid: 'Vendor Code' },
      id: { datagrid: 'chooseVendor' },
      isRequired: { commitForm: true, datagrid: false },
      visible: { commitForm: true },
    },
    {
      controlType: { commitForm: 'd-poNumber' },
      exclude: { datagrid: true },
      headerText: { datagrid: 'PO', splitForm: 'PO' },
      id: { commitHistory: 'PO', datagrid: 'poNumber', splitForm: 'poNumber' },
      isRequired: { commitForm: true, datagrid: false },
      visible: { commitForm: true },
    },
    {
      controlType: { commitForm: 'checkbox' },
      dataType: { commitForm: 'text' },
      exclude: { datagrid: true },
      exclusive: 'new',
      headerText: { datagrid: 'Lock Commit' },
      id: { datagrid: 'withMerge' },
      visible: { commitForm: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'Otm Receipt Type', splitForm: 'Otm Receipt Type' },
      id: {
        commitHistory: 'OtmReceiptType',
        datagrid: 'otmReceiptType',
        splitForm: 'OtmReceiptType',
      },
      visible: { commitForm: true, datagrid: true },
      // exclude: { splitForm: true, bulkEditForm: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: {
        datagrid: 'Otm Transaction ID',
        splitForm: 'Otm Transaction ID',
      },
      id: {
        commitHistory: 'OtmTransactionID',
        datagrid: 'otmTransactionID',
        splitForm: 'OtmTransactionID',
      },
      visible: { commitForm: false, datagrid: true },
      // exclude: { splitForm: true, bulkEditForm: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'Transport ID', splitForm: 'Transport ID' },
      id: { commitHistory: 'TransportID', datagrid: 'transportID', splitForm: 'TransportID' },
      visible: { commitForm: false, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'RQ', splitForm: 'RQ' },
      id: { commitHistory: 'RQ', datagrid: 'rq', splitForm: 'RQ' },
      visible: { commitForm: true, datagrid: true },
      // exclude: { splitForm: true, bulkEditForm: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: {
        commitForm: 'Doc. Date',
        datagrid: 'Doc. Date',
        splitForm: 'Doc. Date',
      },
      id: {
        commitHistory: 'PurchasingDocumentDate',
        datagrid: 'purchasingDocumentDate',
        splitForm: 'PurchasingDocumentDate',
      },
      visible: { commitForm: false, datagrid: true },
      // exclude: { splitForm: true, bulkEditForm: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string' },
      headerText: {
        commitForm: 'Merged Inbound Delivery Number',
        datagrid: 'Merged ID',
        splitForm: 'Merged ID',
      },
      id: {
        commitHistory: 'MergedInboundDeliveryNumber',
        datagrid: 'mergedInboundDeliveryNumber',
        splitForm: 'mergedInboundDeliveryNumber',
      },
      visible: { commitForm: false, datagrid: true },
      // exclude: { bulkEditForm: true},
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: {
        datagrid: 'Instruction ETA Date',
        splitForm: 'Instruction ETA Date',
      },
      id: {
        commitHistory: 'InstructionEtaDate',
        datagrid: 'instructionEtaDate',
        splitForm: 'InstructionEtaDate',
      },
      visible: { commitForm: false, datagrid: true },
      // exclude: { splitForm: true, bulkEditForm: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: {
        datagrid: 'Otm Received Date',
        splitForm: 'Otm Received Date',
      },
      id: {
        commitHistory: 'OtmReceivedDate',
        datagrid: 'otmReceivedDate',
        splitForm: 'OtmReceivedDate',
      },
      visible: { commitForm: false, datagrid: true },
      // exclude: { splitForm: true, bulkEditForm: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: { datagrid: 'Final ETA Date', splitForm: 'Final ETA Date' }, // Changed as per skype request
      id: {
        commitHistory: 'FinalETADate',
        datagrid: 'sapDeliveryDate',
        splitForm: 'SapDeliveryDate',
      },
      visible: { commitForm: false, datagrid: true },
      // exclude: { splitForm: true, bulkEditForm: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'number', datagrid: 'number' },
      headerText: {
        commitForm: 'Merged Quantity',
        datagrid: 'Merged Qty',
        splitForm: 'Merged Quantity',
      },
      id: {
        commitHistory: 'MergedQuantity',
        datagrid: 'mergedQuantity',
        splitForm: 'mergedQuantity',
      },
      visible: { commitForm: false, datagrid: true },
      // exclude: { bulkEditForm: true},
    },
    {
      headerText: {
        datagrid: 'Edit By',
        splitForm: 'Edit by',
      },
      id: { datagrid: 'editby', splitForm: 'editby' },
      // controlType: {commitForm: 'simple'},
      // dataType: {datagrid: 'number', commitForm: 'number'},
      visible: { commitForm: false, datagrid: true },
      // exclude: { bulkEditForm: true},
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: {
        commitForm: 'number',
        datagrid: 'number',
      },
      headerText: {
        datagrid: 'Remaining Quantity',
        splitForm: 'Remaining Quantity',
      },
      id: {
        commitHistory: 'RemainingQuantity',
        datagrid: 'remainingQuantity',
        splitForm: 'RemainingQuantity',
      },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      headerText: { datagrid: 'Date Code', splitForm: 'Date Code' },
      id: { commitHistory: 'CodeDate', datagrid: 'codeDate', splitForm: 'codeDate' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'dateTime' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'dateTime',
      },
      headerText: { datagrid: 'Arrival Date', splitForm: 'Arrival Date' },
      id: { commitHistory: 'ArrivalDate', datagrid: 'arrivalDate', splitForm: 'arrivalDate' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      exclusive: 'edit',
      headerText: { datagrid: 'Created By', splitForm: 'Created By' },
      id: { commitHistory: 'Creator', datagrid: 'creator', splitForm: 'creator' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'date' },
      dataType: {
        commitForm: 'text',
        datagrid: 'dateUnchanged',
        splitForm: 'date',
      },
      exclude: { bulkEditForm: true, commitForm: true, splitForm: true },
      headerText: { datagrid: 'Create Date', splitForm: 'Create Date' }, // Changed as per skype request
      id: { commitHistory: 'createDate', datagrid: 'createDate', splitForm: 'createDate' },
      visible: { commitForm: true, datagrid: true },
    },

    {
      controlType: { commitForm: 'dropdown' },
      dataType: { commitForm: 'text', datagrid: 'stockType', splitForm: 'dropdown' },
      headerText: { datagrid: 'Stock Type', splitForm: 'Stock Type' },
      id: { commitHistory: 'StockType', datagrid: 'stockType', splitForm: 'StockType' },
      //exclusive: 'edit',
      visible: { commitForm: true, datagrid: true },
    },

    {
      controlType: { commitForm: 'checkbox' },
      dataType: {
        commitForm: 'text',
        datagrid: 'checkbox',
        splitForm: 'boolean',
      },
      exclude: { commitForm: true },
      headerText: {
        datagrid: 'Documents',
        //commitForm: 'hasDocuments',
        //splitForm: 'hasDocuments',
      },
      id: { datagrid: 'hasDocuments', splitForm: 'hasDocuments' },
      visible: { commitForm: false, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'Slot Code', splitForm: 'Slot Code' },
      id: { commitHistory: 'slotCode', datagrid: 'slotCode', splitForm: 'slotCode' },
      visible: { commitForm: true, datagrid: true },
    },
    {
      controlType: { commitForm: 'simple' },
      dataType: { datagrid: 'string', splitForm: 'string' },
      exclude: { commitForm: true },
      headerText: { datagrid: 'Planned Order No.', splitForm: 'Planned Order No.' },
      id: { datagrid: 'plannedOrderNumber', splitForm: 'plannedOrderNumber' },
      visible: { commitForm: false, datagrid: true },
    },

    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'ExternalApproval', splitForm: 'ExternalApproval' },
      id: {
        commitHistory: 'extApproval',
        datagrid: 'extApproval',
        splitForm: 'extApproval',
      },
      visible: { commitForm: true, datagrid: true },
    },

    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: { datagrid: 'Hold', splitForm: 'Hold' },
      id: { commitHistory: 'Hold', datagrid: 'hold', splitForm: 'Hold' },
      visible: { commitForm: true, datagrid: true },
    },

    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      headerText: {
        datagrid: 'Original ID',
        splitForm: 'Original ID',
      },
      id: {
        commitHistory: 'parentInboundDeliveryNumber',
        datagrid: 'parentInboundDeliveryNumber',
        splitForm: 'parentInboundDeliveryNumber',
      },
      visible: { commitForm: true, datagrid: true },
    },

    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      exclude: { commitForm: true },
      headerText: {
        datagrid: 'PNDesc',
        splitForm: 'PNDesc',
      },
      id: {
        commitHistory: 'PNDesc',
        datagrid: 'partNumberDescription',
        splitForm: 'PNDesc',
      },
      visible: { commitForm: true, datagrid: true },
    },

    {
      controlType: { commitForm: 'simple' },
      dataType: { commitForm: 'text', datagrid: 'string', splitForm: 'string' },
      exclude: { commitForm: true },
      headerText: {
        datagrid: 'VendorName',
        splitForm: 'VendorName',
      },
      id: {
        commitHistory: 'VendorName',
        datagrid: 'vendorName',
        splitForm: 'VendorName',
      },
      visible: { commitForm: true, datagrid: true },
    },
  ];

  getCommitDocumentsFields(): MappedField[] {
    return this.commitDocuments
      .filter((field: CommitFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: CommitFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getCDFormField(): MappedField[] {
    return this.commitDocuments
      .filter((field: CommitFields): boolean => {
        return this.hasField(field, 'datagrid', 'createCDForm');
      })
      .map((field: CommitFields): MappedField => {
        return {
          controlType: field.controlType?.createCDForm
            ? (field.controlType.createCDForm as string)
            : '',
          field:
            (field.id?.createCDForm
              ? (field.id.createCDForm as string)
              : (field.id?.datagrid as string)) ?? '',
          headerText: (field.headerText?.createCDForm
            ? field.headerText.createCDForm
            : field.headerText?.datagrid) as string,
          isRequired: !!field.isRequired?.createCDForm,
          visible: !!field.visible?.createCDForm,
        };
      });
  }

  getCDEditFormFields(): MappedField[] {
    return this.commitDocuments
      .filter((field: CommitFields): boolean => {
        return this.hasField(field, 'datagrid', 'editCDForm');
      })
      .map((field: CommitFields): MappedField => {
        return {
          controlType: field.controlType?.editCDForm
            ? (field.controlType.editCDForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.editCDForm
            ? field.headerText.editCDForm
            : field.headerText?.datagrid) as string,
          isRequired: !!field.isRequired?.editCDForm,
          visible: !!field.visible?.editCDForm,
        };
      });
  }

  public readonly commitDocuments: CommitFields[] = [
    {
      controlType: { createCDForm: 'file' },
      dataType: { datagrid: 'string' },
      headerText: { createCDForm: 'File Name', datagrid: 'File Name' },
      id: { datagrid: 'name' },
      isRequired: { createCDForm: true },
      visible: { datagrid: true },
    },

    {
      controlType: { createCDForm: 'simple', editCDForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: {
        createCDForm: 'File Description',
        datagrid: 'File Descripton',
        editCDForm: 'File Description',
      },
      id: { datagrid: 'description' },
      isRequired: { createCDForm: true },
      visible: { datagrid: true },
    },

    {
      controlType: { createCDForm: 'datetime' },
      dataType: { datagrid: 'string' },
      headerText: { createCDForm: 'Create Date', datagrid: 'Create Date' },
      id: { datagrid: 'dateCreated' },
      isRequired: { createCDForm: true },
      visible: { datagrid: true },
    },

    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
  ];

  getTemplateFiltersFields(): MappedField[] {
    return this.templateFilters
      .filter((field: CommitFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: CommitFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getTemplateFiltersFormFields(): MappedField[] {
    return this.templateFilters
      .filter((field: CommitFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: CommitFields): MappedField => {
        return {
          controlType: field.controlType?.templateFilterCreateForm
            ? (field.controlType.templateFilterCreateForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.templateFilterCreateForm,
          visible: !!field.visible?.templateFilterCreateForm,
        };
      });
  }

  getTemplateFiltersEditFormFields(): MappedField[] {
    return this.templateFilters
      .filter((field: CommitFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: CommitFields): MappedField => {
        return {
          controlType: field.controlType?.templateFilterEditForm
            ? (field.controlType.templateFilterEditForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.templateFilterEditForm,
          visible: !!field.visible?.templateFilterEditForm,
        };
      });
  }

  public readonly templateFilters: CommitFields[] = [
    {
      controlType: { templateFilterCreateForm: 'simple', templateFilterEditForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Filter Name' },
      id: { datagrid: 'name' },
      isRequired: { templateFilterCreateForm: true, templateFilterEditForm: true },
      visible: { datagrid: true },
    },

    {
      controlType: { templateFilterCreateForm: 'simple', templateFilterEditForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Filter Description' },
      id: { datagrid: 'description' },
      isRequired: { templateFilterCreateForm: true, templateFilterEditForm: true },
      visible: { datagrid: true },
    },

    {
      controlType: { templateFilterCreateForm: 'radio', templateFilterEditForm: 'radio' },
      dataType: { datagrid: 'radio' },
      headerText: { datagrid: 'Default' },
      id: { datagrid: 'isDefault' },
      isRequired: { templateFilterCreateForm: false },
      visible: { datagrid: false },
    },

    {
      controlType: { templateFilterCreateForm: 'toggle', templateFilterEditForm: 'toggle' },
      dataType: { datagrid: 'toggle' },
      headerText: { datagrid: 'Shared' },
      id: { datagrid: 'isShared' },
      isRequired: { templateFilterCreateForm: false },
      visible: { datagrid: false },
    },

    {
      controlType: { templateFilterCreateForm: 'toggle', templateFilterEditForm: 'toggle' },
      dataType: { datagrid: 'toggle' },
      headerText: { datagrid: 'Active' },
      id: { datagrid: 'isActive' },
      isRequired: { templateFilterCreateForm: false },
      visible: { datagrid: true },
    },

    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
  ];

  getChartSettingsFields(): MappedField[] {
    return this.chartSettings
      .filter((field: CommitFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: CommitFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getCSFormField(): MappedField[] {
    return this.chartSettings
      .filter((field: CommitFields): boolean => {
        return this.hasField(field, 'datagrid', 'createCSForm');
      })
      .map((field: CommitFields): MappedField => {
        return {
          controlType: field.controlType?.createCSForm
            ? (field.controlType.createCSForm as string)
            : '',
          field:
            (field.id?.createCSForm
              ? (field.id.createCSForm as string)
              : (field.id?.datagrid as string)) ?? '',
          headerText: (field.headerText?.createCSForm
            ? field.headerText.createCSForm
            : field.headerText?.datagrid) as string,
          isRequired: !!field.isRequired?.createCSForm,
          visible: !!field.visible?.createCSForm,
        };
      });
  }

  getCSEditFormFields(): MappedField[] {
    return this.chartSettings
      .filter((field: CommitFields): boolean => {
        return this.hasField(field, 'datagrid', 'editCSForm');
      })
      .map((field: CommitFields): MappedField => {
        return {
          controlType: field.controlType?.editCSForm
            ? (field.controlType.editCSForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.editCSForm
            ? field.headerText.editCDForm
            : field.headerText?.datagrid) as string,
          isRequired: !!field.isRequired?.editCSForm,
          visible: !!field.visible?.editCSForm,
        };
      });
  }

  public readonly chartSettings: CommitFields[] = [
    {
      controlType: { createCSForm: 'simple', editCSForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { createCSForm: 'Name', datagrid: 'Name', editCSForm: 'Name' },
      id: { datagrid: 'name' },
      isRequired: { createCSForm: true, editCSForm: true },
      visible: { datagrid: true },
    },

    {
      // controlType: { createCSForm: 'toggle'},
      dataType: { datagrid: 'toggle' },
      headerText: { datagrid: 'Active' },
      id: { datagrid: 'isActive' },
      visible: { datagrid: true },
    },

    {
      controlType: { createCSForm: 'dropdown', editCSForm: 'dropdown' },
      headerText: { createCSForm: 'Chart Type', editCSForm: 'Chart Type' },
      id: { datagrid: 'type' },
      isRequired: { createCSForm: true, editCSForm: true },
      // dataType: { datagrid: 'string' },
      visible: { datagrid: false },
    },

    {
      controlType: { createCSForm: 'dropdown', editCSForm: 'dropdown' },
      headerText: { createCSForm: 'Security Levels', editCSForm: 'Security Levels' },
      id: { datagrid: 'securityLevels' },
      isRequired: { createCSForm: true, editCSForm: true },
      visible: { datagrid: false },
    },

    {
      controlType: { createCSForm: 'simple', editCSForm: 'simple' },
      headerText: { createCSForm: 'Qlik app ID', editCSForm: 'Qlik app ID' },
      id: { datagrid: 'qlikAppId' },
      // dataType: { datagrid: 'string' },
      visible: { datagrid: false },
    },

    {
      controlType: { createCSForm: 'simple', editCSForm: 'simple' },
      headerText: { createCSForm: 'Table name', editCSForm: 'Table name' },
      id: { datagrid: 'tableName' },
      // dataType: { datagrid: 'string' },
      visible: { datagrid: false },
    },

    {
      controlType: { createCSForm: 'simple', editCSForm: 'simple' },
      headerText: { createCSForm: 'Date column', editCSForm: 'Date column' },
      id: { datagrid: 'dateKey' },
      // dataType: { datagrid: 'string' },
      visible: { datagrid: false },
    },

    {
      controlType: { createCSForm: 'checkbox', editCSForm: 'checkbox' },
      headerText: { createCSForm: 'Show date as weeks', editCSForm: 'Show date as weeks' },
      id: { datagrid: 'showDateAsWeeks' },
      // dataType: { datagrid: 'string' },
      visible: { datagrid: false },
    },

    {
      controlType: { createCSForm: 'simple', editCSForm: 'simple' },
      headerText: { createCSForm: 'X axis label', editCSForm: 'X axis label' },
      id: { datagrid: 'xAxisLabel' },
      // dataType: { datagrid: 'string' },
      visible: { datagrid: false },
    },

    {
      controlType: { createCSForm: 'simple', editCSForm: 'simple' },
      headerText: { createCSForm: 'Y axis label', editCSForm: 'Y axis label' },
      id: { datagrid: 'yAxisLabel' },
      // dataType: { datagrid: 'string' },
      visible: { datagrid: false },
    },

    {
      controlType: { createCSForm: 'number', editCSForm: 'number' },
      headerText: { createCSForm: 'Order', editCSForm: 'Order' },
      id: { datagrid: 'order' },
      // dataType: { datagrid: 'string' },
      visible: { datagrid: false },
    },

    // {
    //   id: { datagrid: 'seriesName' },
    //   headerText: { createCSForm: 'Name' },
    //   controlType: { createCSForm: 'simple'},
    //   // dataType: { datagrid: 'string' },
    //   visible: { datagrid: false },
    // },

    // {
    //   id: { datagrid: 'nameKey' },
    //   headerText: { createCSForm: 'Name Column Key' },
    //   controlType: { createCSForm: 'simple'},
    //   // dataType: { datagrid: 'string' },
    //   visible: { datagrid: false },
    // },

    // {
    //   id: { datagrid: 'valueKey' },
    //   headerText: { createCSForm: 'Value Column Key' },
    //   controlType: { createCSForm: 'simple'},
    //   // dataType: { datagrid: 'string' },
    //   visible: { datagrid: false },
    // },

    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
  ];
}
